import { z } from 'zod';
import { EmailProtection, ENGAGEMENT_OPTIONS, PostMode, ReceiptChannel, SepaScheme, } from './engagement-choices.js';
/**
 * Which questions of the contract draft the firm asks in every draft, and which it answers once
 * with a standard of its own (ADR-071 § 3, „Fragen im Entwurf“). Part of the firm's catalogue and
 * versioned with it. A standard only fills in a draft: the rule pack `stb-vertrag` checks a
 * contract the same way, however its terms came about.
 */
/**
 * Yes-or-no terms that are facts about the client - whether it keeps cash, draws up a balance
 * sheet, has data of third parties, asks as a consumer for an early start. They are asked in every
 * draft; no firm standard can know them.
 */
const CLIENT_FACTS = [
    'keepsCash',
    'clientAccountsOnBalanceSheet',
    'thirdPartyData',
    'consumerRequestsEarlyStart',
];
export const STANDARD_OPTIONS = ENGAGEMENT_OPTIONS.filter((o) => !CLIENT_FACTS.includes(o));
const standardOptions = Object.fromEntries(STANDARD_OPTIONS.map((o) => [o, z.boolean().nullable()]));
/**
 * Per question: `null` asks it in every draft (as without a catalogue); a value is the firm's
 * standard - a new draft takes it and the question is not asked.
 */
export const DraftQuestions = z
    .object({
    receiptChannel: ReceiptChannel.nullable(),
    sepa: z.boolean().nullable(),
    /** Only for clients that are no consumers - a consumer always pays by the core scheme. */
    sepaScheme: SepaScheme.nullable(),
    emailProtection: EmailProtection.nullable(),
    postMode: PostMode.nullable(),
    options: z.object(standardOptions).strict(),
})
    .strict()
    .meta({ id: 'DraftQuestions' });
/** Every question a firm may answer by its standard. */
export const DRAFT_QUESTIONS = [
    'receiptChannel',
    'sepa',
    'sepaScheme',
    'emailProtection',
    'postMode',
    ...STANDARD_OPTIONS,
];
/** Every question asked - the catalogue before the firm decides anything else. */
export const ASK_EVERYTHING = {
    receiptChannel: null,
    sepa: null,
    sepaScheme: null,
    emailProtection: null,
    postMode: null,
    options: Object.fromEntries(STANDARD_OPTIONS.map((o) => [o, null])),
};
/**
 * The questions of a saved catalogue: a version saved before there were any asks everything, and
 * a question added since is asked - nothing is answered that the firm did not answer.
 */
export function draftQuestionsOf(saved) {
    const options = saved?.options;
    return {
        receiptChannel: saved?.receiptChannel ?? null,
        sepa: saved?.sepa ?? null,
        sepaScheme: saved?.sepaScheme ?? null,
        emailProtection: saved?.emailProtection ?? null,
        postMode: saved?.postMode ?? null,
        options: Object.fromEntries(STANDARD_OPTIONS.map((o) => [o, options?.[o] ?? null])),
    };
}
const isOption = (question) => STANDARD_OPTIONS.includes(question);
/** The firm's standard for a question; `null`: it is asked. */
export const standardOf = (questions, question) => isOption(question) ? questions.options[question] : questions[question];
/** How many questions the firm answers by its standard. */
export const standardCount = (questions) => DRAFT_QUESTIONS.filter((q) => standardOf(questions, q) !== null).length;
/** The questions with one standard set, or asked again (`null`); the others as they were. */
export const withStandard = (questions, question, value) => DraftQuestions.parse(isOption(question)
    ? { ...questions, options: { ...questions.options, [question]: value } }
    : { ...questions, [question]: value });
//# sourceMappingURL=draft-questions.js.map